import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, UserPlus, Loader2, Ban, RotateCcw, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { RoleChip, ROLE_META } from "@/components/admin/roleMeta";

const TEAM_ROLES = ["root", "staff_edit", "staff_view"];

/**
 * Team tab — internal PlateProfit staff (root + staff_edit + staff_view).
 * Root can mint new admins, promote/demote, suspend.
 */
export default function TeamTab({ me, registerRefresh }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [busyUserId, setBusyUserId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const isRoot = me?.role === "root";
  const canWrite = me?.role === "root" || me?.role === "staff_edit";

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token || ""}` };
  }, []);

  // Fetch staff/root by calling /admin/users 3 times — once per role.  Cheap
  // and keeps server-side pagination semantics clean.
  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const h = await authHeaders();
      const fetches = TEAM_ROLES.map((r) =>
        axios.get(`${API}/admin/users?page=1&page_size=200&role=${r}`, { headers: h })
          .then((res) => res.data.users || [])
          .catch(() => [])
      );
      const grouped = await Promise.all(fetches);
      let flat = grouped.flat();
      if (roleFilter) flat = flat.filter((u) => u.role === roleFilter);
      if (q) {
        const needle = q.toLowerCase();
        flat = flat.filter((u) =>
          (u.email || "").toLowerCase().includes(needle) ||
          (u.full_name || "").toLowerCase().includes(needle)
        );
      }
      setMembers(flat);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, q, roleFilter]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchTeam);
  }, [registerRefresh, fetchTeam]);

  const changeRole = async (userId, newRole) => {
    setBusyUserId(userId);
    try {
      const h = await authHeaders();
      await axios.patch(`${API}/admin/users/${userId}`, { role: newRole }, { headers: h });
      toast.success(`Role updated → ${newRole}`);
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusyUserId(null);
    }
  };

  const suspendUser = async (userId) => {
    if (!window.confirm("Suspend this team member? They'll lose access immediately.")) return;
    setBusyUserId(userId);
    try {
      const h = await authHeaders();
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, { headers: h });
      toast.success("Team member suspended");
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suspend failed");
    } finally {
      setBusyUserId(null);
    }
  };

  const restoreUser = async (userId) => {
    setBusyUserId(userId);
    try {
      const h = await authHeaders();
      await axios.post(`${API}/admin/users/${userId}/restore`, {}, { headers: h });
      toast.success("Team member restored");
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="hq-tab-team">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search team email or name…"
            className="pl-9 bg-slate-900 border-slate-700 text-white"
            data-testid="hq-team-search"
          />
        </div>
        <div className="flex gap-1">
          {["", "root", "staff_edit", "staff_view"].map((r) => (
            <button
              key={r || "all"}
              type="button"
              onClick={() => setRoleFilter(r)}
              className={cn(
                "px-3 py-1.5 rounded text-xs font-semibold border transition-colors",
                roleFilter === r
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-slate-900 text-slate-400 border-slate-700 hover:border-emerald-500/50"
              )}
              data-testid={"hq-team-filter-" + (r || "all")}
            >
              {r ? ROLE_META[r].label : "All"}
            </button>
          ))}
        </div>
        {isRoot && (
          <Button
            size="sm"
            onClick={() => setShowInvite(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            data-testid="hq-team-invite-btn"
          >
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Invite Team Member
          </Button>
        )}
        <span className="text-xs text-slate-500 ml-1 font-mono">{members.length} members</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 border-b border-slate-800">
            <tr className="text-left text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
              <th className="px-4 py-2.5 font-semibold">Member</th>
              <th className="px-4 py-2.5 font-semibold">Role</th>
              <th className="px-4 py-2.5 font-semibold">Joined</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="hq-team-tbody">
            {loading ? (
              <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : members.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">No team members match.</td></tr>
            ) : members.map((u) => (
              <tr
                key={u.user_id}
                className={cn(
                  "border-b border-slate-800 last:border-0 hover:bg-slate-900/80",
                  u.disabled_at && "opacity-40"
                )}
                data-testid={"hq-team-row-" + u.user_id}
              >
                <td className="px-4 py-2.5">
                  <div className="text-white text-xs font-semibold">{u.email || u.full_name || "—"}</div>
                  <div className="text-[0.65rem] text-slate-500 font-mono">{u.user_id.slice(0, 8)}…</div>
                </td>
                <td className="px-4 py-2.5"><RoleChip role={u.role} /></td>
                <td className="px-4 py-2.5 text-[0.7rem] font-mono text-slate-400">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {u.disabled_at ? (
                    <span className="text-rose-400 inline-flex items-center gap-1"><Ban className="h-3 w-3" />Suspended</span>
                  ) : (
                    <span className="text-emerald-400 inline-flex items-center gap-1">Active</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex flex-wrap gap-1 justify-end">
                    {isRoot && u.role !== "root" && (
                      <button
                        type="button"
                        onClick={() => changeRole(u.user_id, "root")}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                        data-testid={"hq-team-promote-root-" + u.user_id}
                        title="Promote to root"
                      >↑Root</button>
                    )}
                    {canWrite && u.role !== "staff_edit" && (
                      <button
                        type="button"
                        onClick={() => changeRole(u.user_id, "staff_edit")}
                        disabled={busyUserId === u.user_id || u.user_id === me?.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        data-testid={"hq-team-set-edit-" + u.user_id}
                        title="Set role to staff_edit"
                      >Edit</button>
                    )}
                    {canWrite && u.role !== "staff_view" && (
                      <button
                        type="button"
                        onClick={() => changeRole(u.user_id, "staff_view")}
                        disabled={busyUserId === u.user_id || u.user_id === me?.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 disabled:opacity-50"
                        data-testid={"hq-team-set-view-" + u.user_id}
                        title="Set role to staff_view (read-only)"
                      >View</button>
                    )}
                    {isRoot && u.role !== "chef" && u.user_id !== me?.user_id && (
                      <button
                        type="button"
                        onClick={() => changeRole(u.user_id, "chef")}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-slate-700 text-slate-400 hover:bg-slate-800 disabled:opacity-50"
                        data-testid={"hq-team-demote-chef-" + u.user_id}
                        title="Demote to chef (out of team)"
                      >↓Chef</button>
                    )}
                    {isRoot && !u.disabled_at && u.user_id !== me?.user_id && u.role !== "root" && (
                      <button
                        type="button"
                        onClick={() => suspendUser(u.user_id)}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        data-testid={"hq-team-suspend-" + u.user_id}
                        title="Suspend team member"
                      ><Ban className="h-3 w-3 inline" /></button>
                    )}
                    {canWrite && u.disabled_at && (
                      <button
                        type="button"
                        onClick={() => restoreUser(u.user_id)}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        data-testid={"hq-team-restore-" + u.user_id}
                        title="Restore team member"
                      ><RotateCcw className="h-3 w-3 inline" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md bg-slate-900 border-emerald-500/30" data-testid="hq-invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-emerald-400" />
              Invite Team Member
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Creates an auth user + team profile. Share the temp password manually.
            </DialogDescription>
          </DialogHeader>
          <InviteForm
            onSuccess={() => { setShowInvite(false); fetchTeam(); }}
            onClose={() => setShowInvite(false)}
            authHeaders={authHeaders}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InviteForm({ onSuccess, onClose, authHeaders }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("staff_view");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const h = await authHeaders();
      const res = await axios.post(
        `${API}/admin/users/invite`,
        { email: email.trim(), password, role, full_name: fullName || null },
        { headers: h }
      );
      toast.success(`Created ${res.data.email} as ${res.data.role}`);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="block text-[0.7rem] font-semibold text-slate-400 mb-1">Email</label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
               className="bg-slate-950 border-slate-700 text-white" data-testid="hq-invite-email" />
      </div>
      <div>
        <label className="block text-[0.7rem] font-semibold text-slate-400 mb-1">Full name (optional)</label>
        <Input value={fullName} onChange={(e) => setFullName(e.target.value)}
               className="bg-slate-950 border-slate-700 text-white" data-testid="hq-invite-fullname" />
      </div>
      <div>
        <label className="block text-[0.7rem] font-semibold text-slate-400 mb-1">Temporary password (≥8 chars)</label>
        <Input type="text" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required
               className="bg-slate-950 border-slate-700 text-white font-mono" data-testid="hq-invite-password" />
        <p className="text-[0.65rem] text-slate-500 mt-1">
          You&apos;ll share this with the staff member directly. Have them change it on first login.
        </p>
      </div>
      <div>
        <label className="block text-[0.7rem] font-semibold text-slate-400 mb-1">Role</label>
        <div className="flex gap-2">
          {[
            { k: "staff_view", desc: "Read-only · accountants" },
            { k: "staff_edit", desc: "Modify users · IT" },
            { k: "root",       desc: "Full power" },
          ].map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => setRole(r.k)}
              className={cn(
                "flex-1 px-2 py-1.5 rounded text-[0.7rem] font-semibold border transition-colors flex flex-col items-center gap-0.5",
                role === r.k
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-slate-900 text-slate-400 border-slate-700"
              )}
              data-testid={"hq-invite-role-" + r.k}
            >
              <span>{ROLE_META[r.k].label}</span>
              <span className="text-[0.55rem] opacity-70 normal-case font-normal">{r.desc}</span>
            </button>
          ))}
        </div>
      </div>
      {error && (
        <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2">
          {error}
        </div>
      )}
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onClose}
                className="bg-slate-800 border-slate-700 text-slate-300" data-testid="hq-invite-cancel">
          <X className="h-3.5 w-3.5 mr-1" />Cancel
        </Button>
        <Button type="submit" disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="hq-invite-submit">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
          Create
        </Button>
      </DialogFooter>
    </form>
  );
}
